import supabase from '../supabaseClient';

export const SUBSCRIPTION_PRICE_CENTS = 4000;
export const SUBSCRIPTION_CURRENCY = 'USD';
export const SUBSCRIPTION_PACKAGE_ID = 'subscription_monthly';

const COMMUNITY_FIELDS = [
  'id',
  'billing_status',
  'subscription_started_at',
  'subscription_current_period_start',
  'subscription_current_period_end',
  'subscription_canceled_at',
  'subscription_amount_cents',
  'subscription_currency',
].join(', ');

const addOneMonth = (date) => {
  const d = new Date(date.getTime());
  const day = d.getDate();
  d.setMonth(d.getMonth() + 1);
  // Handle months with fewer days (e.g. Jan 31 -> Feb 28).
  if (d.getDate() < day) d.setDate(0);
  return d;
};

const toDate = (value) => {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

export function getSubscriptionState(community) {
  if (!community) {
    return { status: 'inactive', isActive: false, isCanceled: false, isPastDue: false, periodEnd: null, daysRemaining: null };
  }
  const status = community.billing_status || 'inactive';
  const periodEnd = toDate(community.subscription_current_period_end);
  const now = new Date();
  const hasFuturePeriod = !!periodEnd && periodEnd.getTime() > now.getTime();
  const isActive = status === 'active' && hasFuturePeriod;
  const isCanceled = status === 'canceled' || !!community.subscription_canceled_at;
  const isPastDue = status === 'past_due' || (status === 'active' && !!periodEnd && !hasFuturePeriod);
  const daysRemaining = periodEnd
    ? Math.max(0, Math.ceil((periodEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)))
    : null;
  return {
    status,
    isActive,
    isCanceled,
    isPastDue,
    hasAccess: isActive || (isCanceled && hasFuturePeriod),
    periodEnd,
    daysRemaining,
  };
}

export function isSubscriptionActive(community) {
  return getSubscriptionState(community).isActive;
}

export async function fetchCommunitySubscription(communityId) {
  if (!communityId) return null;
  const { data, error } = await supabase
    .from('communities')
    .select(COMMUNITY_FIELDS)
    .eq('id', communityId)
    .single();
  if (error) {
    console.error('fetchCommunitySubscription:', error);
    return null;
  }
  return data;
}

/**
 * Activate (or renew) a monthly subscription for the community.
 * Sets billing_status='active', extends the current period by one month,
 * and writes a credit_transactions purchase row for the $40 charge.
 *
 * NOTE: this assumes payment has already succeeded upstream (or is being
 * recorded manually). Hook the actual CyberSource charge in before calling
 * this function in production.
 */
export async function subscribeCommunity(communityId, opts = {}) {
  if (!communityId) throw new Error('communityId is required');
  const amountCents = Number.isFinite(opts.amountCents) ? opts.amountCents : SUBSCRIPTION_PRICE_CENTS;
  const currency = opts.currency || SUBSCRIPTION_CURRENCY;

  const current = await fetchCommunitySubscription(communityId);
  const now = new Date();
  const existingEnd = toDate(current?.subscription_current_period_end);
  // If the user is renewing while still inside their paid period, extend from
  // the existing end date. Otherwise start a fresh period from now.
  const periodStart = existingEnd && existingEnd.getTime() > now.getTime() ? existingEnd : now;
  const periodEnd = addOneMonth(periodStart);

  const updates = {
    billing_status: 'active',
    subscription_current_period_start: periodStart.toISOString(),
    subscription_current_period_end: periodEnd.toISOString(),
    subscription_canceled_at: null,
    subscription_amount_cents: amountCents,
    subscription_currency: currency,
  };
  if (!current?.subscription_started_at) {
    updates.subscription_started_at = now.toISOString();
  }

  const { data: updated, error: updateErr } = await supabase
    .from('communities')
    .update(updates)
    .eq('id', communityId)
    .select(COMMUNITY_FIELDS)
    .single();
  if (updateErr) throw updateErr;

  // Record the invoice in credit_transactions for the billing history.
  let userId = opts.userId;
  if (!userId) {
    const { data: authData } = await supabase.auth.getUser();
    userId = authData?.user?.id || null;
  }
  if (userId) {
    const { error: txErr } = await supabase.from('credit_transactions').insert({
      user_id: userId,
      community_id: communityId,
      transaction_type: 'purchase',
      credits: 0,
      package_id: SUBSCRIPTION_PACKAGE_ID,
      amount_cents: amountCents,
      currency,
      reference_code: opts.referenceCode || `sub_${Date.now()}`,
    });
    if (txErr) console.error('subscribeCommunity: failed to record invoice', txErr);
  }
  return updated;
}

/**
 * Cancel the subscription. The community keeps access until
 * subscription_current_period_end and then becomes inactive on next renewal.
 */
export async function cancelSubscription(communityId) {
  if (!communityId) throw new Error('communityId is required');
  const now = new Date();
  const { data, error } = await supabase
    .from('communities')
    .update({
      billing_status: 'canceled',
      subscription_canceled_at: now.toISOString(),
    })
    .eq('id', communityId)
    .select(COMMUNITY_FIELDS)
    .single();
  if (error) throw error;
  return data;
}

/**
 * Reactivate a canceled subscription that still has period remaining
 * (no new charge). For an expired one, call subscribeCommunity instead.
 */
export async function reactivateSubscription(communityId) {
  if (!communityId) throw new Error('communityId is required');
  const { data, error } = await supabase
    .from('communities')
    .update({
      billing_status: 'active',
      subscription_canceled_at: null,
    })
    .eq('id', communityId)
    .select(COMMUNITY_FIELDS)
    .single();
  if (error) throw error;
  return data;
}
